// messageService.js
// API layer for the per-job dealer↔specialist message thread.
//
// The thread is a CONSOLIDATED JOB TIMELINE: the server merges human messages
// (job_messages, seeded with the request's creation note as message #1) with
// lifecycle events projected from request_state. The client consumes one
// ordered-ascending array of timeline items discriminated by `kind`:
//   { kind: "message", message_id, request_id, body, sender_user_type,
//     sender_user_id, sender_name, created_at, is_seed_note }
//   { kind: "event",   event_id, request_id, text, actor_user_type, created_at }
//
// Transport is async (no websockets): the ConversationDrawer polls listMessages
// while open; unread counts ride on the existing list endpoints, so there is no
// count-fetch here.

import { HTTPService, generateIdempotencyKey } from './httpService';

const API = new HTTPService();

/**
 * Fetch the full timeline for a request, ordered ascending (oldest first).
 * Message #1 is the seeded creation note.
 * @param {number|string} requestId
 * @returns {Promise<Array>} timeline items (kind: "message" | "event")
 */
export const listMessages = async (requestId) => {
  const { data } = await API.get(`/requests/${requestId}/messages`);
  return data.data || data;
};

/**
 * Post a new human message to the thread. Idempotency-keyed so a retry never
 * double-posts. Sender identity is sent explicitly (user_id/user_type) — NOT
 * derived from the token. requestId is path-only.
 * @param {number|string} requestId
 * @param {string} body
 * @param {number|string} userId   the logged-in user's id
 * @param {string} userType        'dealer' | 'installer'
 * @returns {Promise<Object>} the saved message (kind: "message")
 */
export const postMessage = async (requestId, body, userId, userType) => {
  const { data } = await API.post(
    `/requests/${requestId}/messages`,
    { body, user_id: userId, user_type: userType },
    { idempotencyKey: generateIdempotencyKey() }
  );
  return data.data || data;
};

/**
 * Mark the thread read for the current user up to a given message.
 * Read-state is per-user — the client passes the logged-in user's id and type.
 * @param {number|string} requestId
 * @param {number|string} lastReadMessageId
 * @param {number|string} userId   the logged-in user's id
 * @param {string} userType        'dealer' | 'installer'
 * @returns {Promise<Object>} { request_id, unread_count: 0 }
 */
export const markRead = async (requestId, lastReadMessageId, userId, userType) => {
  const { data } = await API.post(
    `/requests/${requestId}/messages/read`,
    { last_read_message_id: lastReadMessageId, user_id: userId, user_type: userType }
  );
  return data.data || data;
};

const MessageAPI = { listMessages, postMessage, markRead };

export default MessageAPI;
